import React from 'react'
import styled, { css } from 'styled-components'

// Styles
import { applyTextStyle } from '../styles/typography'
import { FontWeight } from '../common/models/typography'
import { fastTransition, shadows } from '../styles/effects'
import { mqFrom } from '../styles/mediaQueries'
import { contrastColorOn } from '../styles/colors'

// Components
import AmountTag from './tag/AmountTag'
import Button from './Button'
import Icon from './icon/Icon'
import Text from './Text'

// Types
import { IconType } from './icon/IconType'

interface ITabsProps {
  children: React.ReactNode
  className?: string
}

type ILargeTabProps = Pick<
  ITabProps,
  'selected' | 'label' | 'className' | 'onClick'
> & {
  caption?: string
  icon?: IconType
  'data-cy'?: string
}

interface ITabProps {
  label: string
  tagAmount?: number
  selected?: boolean
  disabled?: boolean
  onClick?: (e?: any) => void
  className?: string
  tabColor?: string
  textColor?: string
  defaultTabColor?: string
  /** A dot rather than a count: "something new here", without quantifying it. */
  showDot?: boolean
}

interface INavTabProps
  extends Pick<
    ITabProps,
    | 'label'
    | 'tagAmount'
    | 'selected'
    | 'disabled'
    | 'onClick'
    | 'className'
    | 'showDot'
  > {}

// A tab is an <li> for layout reasons, so it needs the affordances a button gets
// for free: reachable by Tab, activated by Enter or Space.
const interactionProps = (
  props: Pick<ITabProps, 'selected' | 'disabled' | 'onClick'>
) => ({
  role: 'button',
  'aria-pressed': props.selected,
  'aria-disabled': props.disabled,
  tabIndex: props.disabled ? -1 : 0,
  onClick: props.onClick,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (props.disabled || (event.key !== 'Enter' && event.key !== ' ')) {
      return
    }
    event.preventDefault()
    props.onClick?.()
  },
})

export const Tab = (props: ITabProps) => (
  <StyledTab
    {...interactionProps(props)}
    className={props.className}
    selected={props.selected}
    disabled={props.disabled}
    tabColor={props.tabColor}
    defaultTabColor={props.defaultTabColor}
  >
    <TabLabel textColor={props.textColor}>{props.label}</TabLabel>
    {props.tagAmount !== undefined && (
      <StyledAmountTag size="small" amount={props.tagAmount} color="text" />
    )}
    {props.showDot && <TabDot $ringed={props.selected} />}
  </StyledTab>
)

/**
 * The underlined tab strip of SecondaryNavigation, at a size that fits inside a
 * panel. Use where tabs switch a view in place; `Tab` (a filled pill) reads as a
 * filter, which is a different promise.
 */
export const NavTab = (props: INavTabProps) => (
  <StyledNavTab
    {...interactionProps(props)}
    className={props.className}
    selected={props.selected}
    disabled={props.disabled}
  >
    {props.label}
    {props.tagAmount !== undefined && (
      <StyledAmountTag size="small" amount={props.tagAmount} color="text" />
    )}
    {props.showDot && <TabDot />}
  </StyledNavTab>
)

export const NavTabs = (props: ITabsProps) => (
  <NavTabsWrapper className={props.className}>{props.children}</NavTabsWrapper>
)

export const Tabs = (props: ITabsProps) => {
  return <TabsWrapper className={props.className}>{props.children}</TabsWrapper>
}

export const PageTab = (props: ITabProps) => (
  <StyledButton
    className={props.className}
    disabled={props.disabled}
    color={!props.selected ? 'primaryFadeLight' : 'primary'}
    textColor={!props.selected ? 'text' : 'lightest'}
    hoverColor={!props.selected ? 'primaryFadeHalf' : 'primary'}
    hoverTextColor={'lightest'}
    onClick={props.onClick}
    caption={props.label}
  />
)

export const PageTabs = (props: ITabsProps) => (
  <PageTabsContainer className={props.className}>
    <PageTabsWrapper>{props.children}</PageTabsWrapper>
  </PageTabsContainer>
)

export const LargeTab = ({
  className,
  label,
  icon,
  selected,
  caption,
  onClick,
  'data-cy': dataCy,
}: ILargeTabProps) => (
  <LargeStyledTab
    className={className}
    onClick={onClick}
    selected={selected}
    data-cy={dataCy}
  >
    {icon && <StyledIcon type={icon} size={40} />}
    <Text
      textStyle="displayS"
      marginTop={16}
      textAlign="center"
      color={selected ? 'primary' : 'text'}
    >
      {label}
    </Text>
    {caption && (
      <Text textStyle="labelSmallCaps" textAlign="center" color="textLighter">
        {caption}
      </Text>
    )}
  </LargeStyledTab>
)

// Shared styles
const sharedWrapperStyles = css`
  display: flex;
  margin: 0;
  width: 100%;
  padding: 0;
  list-style-type: none;
  white-space: nowrap;
`

const sharedTabStyles = css<Pick<ITabProps, 'selected' | 'disabled'>>`
  opacity: ${({ disabled }) => (disabled ? 0.32 : 1)};
  transition: background-color ${fastTransition};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const sharedLabelStyles = css`
  transition: color ${fastTransition};
  color: ${({ theme }) => theme.colors.text};
`

const sharedAmountTagStyles = css`
  margin-left: 8px;
  padding: 0 6px;
  font-weight: ${FontWeight.Regular};
`

// Main styles
const TabsWrapper = styled.ul`
  ${sharedWrapperStyles}
  flex-wrap: wrap;
`

const StyledIcon = styled(Icon)`
  margin: 0;
`

const LargeStyledTab = styled.li<Pick<ITabProps, 'selected'>>`
  ${sharedTabStyles};
  border-radius: 12px;
  padding: 24px 12px 16px;
  background-color: ${({ theme }) => theme.colors.lightest};
  min-width: 136px;
  box-shadow: ${shadows.depthTwo};
  display: flex;
  flex-direction: column;
  align-items: center;

  svg > path {
    fill: ${({ theme, selected }) =>
      selected ? theme.colors.primary : theme.colors.textLighter};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryLight};
    box-shadow: ${shadows.depthThree};

    svg > path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }

  ${({ selected, theme }) =>
    selected &&
    `
    border-bottom: 12px solid ${theme.colors.primary};
  `}
`

const StyledButton = styled(Button)`
  margin-right: 8px;
  padding: 8px;

  &:last-of-type {
    margin-right: 0;
  }

  ${mqFrom.M`
    width: calc(33.33% - 6px);
  `}

  &:hover {
    box-shadow: ${shadows.depthTwo};
  }
`

const PageTabsContainer = styled.div`
  overflow-x: scroll;
  margin: 0 -16px 40px;
  padding: 0 16px;

  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`

const PageTabsWrapper = styled.ul`
  ${sharedWrapperStyles}
  flex-wrap: nowrap;
  display: inline-flex;
  width: auto;
  min-width: 100%;
  padding-bottom: 16px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.textLightest};
`

const StyledTab = styled.li<
  Pick<ITabProps, 'selected' | 'disabled' | 'tabColor' | 'defaultTabColor'>
>`
  ${sharedTabStyles}
  display: inline-block;
  margin-bottom: 8px;
  padding: 4px 8px;
  border-radius: 8px;
  background-color: ${({ theme, defaultTabColor }) =>
    defaultTabColor || theme.colors.lightest};

  &:not(:last-of-type) {
    margin-right: 16px;
  }

  ${({ disabled, selected, theme, tabColor }) =>
    !disabled &&
    !selected &&
    `
    &:hover {
      background-color: ${tabColor || theme.colors.primaryFadeHalf};

      > span {
        color: ${
          tabColor
            ? theme.colors[contrastColorOn(tabColor)]
            : theme.colors.lightest
        };
      }
    }
  `}

  ${({ selected, tabColor, theme }) =>
    selected &&
    css`
      background-color: ${tabColor || theme.colors.primary};

      > span {
        color: ${tabColor
          ? theme.colors[contrastColorOn(tabColor)]
          : theme.colors.lightest};
      }
    `}
`

const NavTabsWrapper = styled.ul`
  ${sharedWrapperStyles};
  align-items: center;
  gap: 20px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.textLightest};
`

const StyledNavTab = styled.li<Pick<ITabProps, 'selected' | 'disabled'>>`
  ${sharedTabStyles};
  ${applyTextStyle('labelHeavy')};
  text-transform: uppercase;
  /* The ratio labelSmallCaps uses: uppercase needs the extra air to stay readable. */
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  padding: 14px 0;
  /* Pulls the tab's own line over the strip's, so only the active one shows. */
  margin-bottom: -2px;
  border-bottom: 2px solid
    ${({ selected, theme }) =>
      selected ? theme.colors.primary : 'transparent'};
  /* Selected label and its underline carry the same primary; the rest sit in the
     default text colour until hovered. */
  color: ${({ selected, theme }) =>
    selected ? theme.colors.primary : theme.colors.text};
  transition: color ${fastTransition}, border-color ${fastTransition};

  &:hover {
    color: ${({ theme, disabled }) =>
      disabled ? theme.colors.text : theme.colors.primary};
  }
`

// Presence, not quantity — the number, where there is one, lives on the bell.
const TabDot = styled.span<{ $ringed?: boolean }>`
  display: inline-block;
  vertical-align: middle;
  /* Red on a selected tab's primary fill needs the ring to stay visible */
  ${({ $ringed, theme }) =>
    $ringed && `box-shadow: 0 0 0 2px ${theme.colors.lightest};`}
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary};
`

const TabLabel = styled.span<{ textColor?: string }>`
  ${sharedLabelStyles}
  ${applyTextStyle('labelHeavy')}
  color: ${({ textColor, theme }) =>
    textColor ? textColor : theme.colors.text};
`

const StyledAmountTag = styled(AmountTag)`
  ${sharedAmountTagStyles}
  background-color: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.text};
`
